import { useEffect, useRef, useState } from "react";

const COLORS = [
  { name: "yellow",  hex: "#facc15" },
  { name: "red",     hex: "#f43f5e" },
  { name: "green",   hex: "#10b981" },
  { name: "blue",    hex: "#3b82f6" },
  { name: "purple",  hex: "#a78bfa" },
  { name: "pink",    hex: "#f472b6" },
  { name: "white",   hex: "#f8fafc" },
];

/**
 * DrawingToolkit — full-screen canvas overlay anchored to the chart panel.
 * Supports freehand drawing in 7 colors + eraser. When `active=false` the overlay
 * is pointer-events:none so the underlying chart stays fully interactive.
 */
export default function DrawingToolkit({ active, onClose, onToggleActive }) {
  const canvasRef = useRef(null);
  const ctxRef = useRef(null);
  const drawingRef = useRef(false);
  const lastPtRef = useRef(null);
  const [color, setColor] = useState(COLORS[0].hex);
  const [size, setSize] = useState(3);
  const [tool, setTool] = useState("pen"); // "pen" | "eraser"
  const [locked, setLocked] = useState(false);

  // Resize canvas to match its container on mount + resize
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const resize = () => {
      const rect = c.parentElement.getBoundingClientRect();
      // Preserve existing pixels by snapshotting
      const snapshot = ctxRef.current ? c.toDataURL() : null;
      c.width = rect.width;
      c.height = rect.height;
      const ctx = c.getContext("2d");
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctxRef.current = ctx;
      if (snapshot) {
        const img = new Image();
        img.onload = () => ctx.drawImage(img, 0, 0);
        img.src = snapshot;
      }
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(c.parentElement);
    return () => ro.disconnect();
  }, []);

  function getPt(e) {
    const rect = canvasRef.current.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: t.clientX - rect.left, y: t.clientY - rect.top };
  }

  function start(e) {
    if (!active) return;
    e.preventDefault();
    drawingRef.current = true;
    lastPtRef.current = getPt(e);
  }
  function move(e) {
    if (!active || !drawingRef.current) return;
    e.preventDefault();
    const ctx = ctxRef.current;
    const pt = getPt(e);
    ctx.beginPath();
    ctx.moveTo(lastPtRef.current.x, lastPtRef.current.y);
    ctx.lineTo(pt.x, pt.y);
    if (tool === "eraser") {
      ctx.globalCompositeOperation = "destination-out";
      ctx.lineWidth = size * 4;
      ctx.strokeStyle = "rgba(0,0,0,1)";
    } else {
      ctx.globalCompositeOperation = "source-over";
      ctx.lineWidth = size;
      ctx.strokeStyle = color;
    }
    ctx.stroke();
    lastPtRef.current = pt;
  }
  function end() {
    drawingRef.current = false;
    lastPtRef.current = null;
  }
  function clearAll() {
    const c = canvasRef.current;
    ctxRef.current?.clearRect(0, 0, c.width, c.height);
  }

  return (
    <>
      <canvas
        ref={canvasRef}
        data-testid="drawing-canvas"
        className="absolute inset-0 z-20"
        style={{ pointerEvents: active ? "auto" : "none", cursor: active ? (tool === "eraser" ? "cell" : "crosshair") : "default" }}
        onMouseDown={start}
        onMouseMove={move}
        onMouseUp={end}
        onMouseLeave={end}
        onTouchStart={start}
        onTouchMove={move}
        onTouchEnd={end}
      />
      {/* Toolbar (always visible so user can toggle activation) */}
      <div
        data-testid="drawing-toolbar"
        className={`absolute bottom-4 left-1/2 -translate-x-1/2 z-30 flex items-center gap-1.5 bg-slate-900/95 border border-slate-700 rounded-full px-3 py-2 backdrop-blur shadow-xl font-mono ${active ? "" : "opacity-80"}`}>
        <button data-testid="draw-toggle" onClick={onToggleActive}
          title={active ? "Disable drawing (click chart again)" : "Enable drawing"}
          className={`text-[10px] px-3 py-1 rounded-full font-black uppercase tracking-wider ${active ? "bg-amber-400 text-slate-900" : "bg-slate-800 text-slate-300 hover:bg-slate-700"}`}>
          <i className={`fa-solid ${active ? "fa-pause" : "fa-pencil"} mr-1`}/> {active ? "Drawing" : "Draw"}
        </button>
        <div className="w-px h-5 bg-slate-700"/>
        {COLORS.map(c => (
          <button key={c.name} data-testid={`color-${c.name}`}
            onClick={() => { setColor(c.hex); setTool("pen"); }}
            className={`w-5 h-5 rounded-full border-2 transition-transform ${color === c.hex && tool === "pen" ? "border-white scale-125" : "border-slate-700"}`}
            style={{ backgroundColor: c.hex }}
            title={c.name}
          />
        ))}
        <div className="w-px h-5 bg-slate-700"/>
        <button data-testid="tool-eraser" onClick={() => setTool("eraser")}
          className={`text-[10px] px-2 py-1 rounded-full font-bold ${tool === "eraser" ? "bg-rose-500/40 text-rose-100" : "text-slate-400 hover:text-white"}`}>
          <i className="fa-solid fa-eraser"/>
        </button>
        <input data-testid="brush-size" type="range" min="1" max="12" value={size}
          onChange={e => setSize(parseInt(e.target.value))} className="w-20 accent-amber-400" title="Brush size"/>
        <div className="w-px h-5 bg-slate-700"/>
        <button data-testid="draw-lock" onClick={() => setLocked(l => !l)}
          title={locked ? "Drawings are locked to canvas" : "Lock drawings"}
          className={`text-[10px] px-2 py-1 rounded-full font-bold ${locked ? "text-amber-300 bg-amber-500/20" : "text-slate-400 hover:text-white"}`}>
          <i className={`fa-solid ${locked ? "fa-lock" : "fa-lock-open"}`}/>
        </button>
        <button data-testid="draw-clear" onClick={() => { if (!locked) clearAll(); }}
          className={`text-[10px] px-2 py-1 rounded-full font-bold ${locked ? "text-slate-700 cursor-not-allowed" : "text-slate-400 hover:text-rose-300"}`}>
          <i className="fa-solid fa-trash"/>
        </button>
        <button data-testid="draw-close" onClick={onClose}
          className="text-[10px] px-2 py-1 rounded-full text-slate-400 hover:text-white font-bold">
          <i className="fa-solid fa-xmark"/>
        </button>
      </div>
    </>
  );
}
